import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreVertical,
  FileEdit,
  Trash2,
  Star,
  File,
  Copy,
  Undo
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { UnifiedPortfolioItem } from "@/types/unified-portfolio";

interface UnifiedPortfolioCardProps {
  item: UnifiedPortfolioItem;
  index: number;
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  onSetFeatured: (id: string) => void;
  onUpdateStatus: (id: string, status: "draft" | "published") => void;
  onDuplicate: (id: string) => void;
  onPreview?: (item: UnifiedPortfolioItem) => void;
  sortOrder: 'last_added' | 'last_edited' | 'alphabetical' | 'featured_first' | 'most_viewed';
  isDraggable?: boolean;
  isProcessing?: boolean;
}

export const UnifiedPortfolioCard: React.FC<UnifiedPortfolioCardProps> = ({
  item,
  index,
  onRemove,
  onEdit,
  onRename,
  onSetFeatured,
  onUpdateStatus,
  onDuplicate,
  onPreview,
  sortOrder,
  isDraggable = false,
  isProcessing = false,
}) => {
  const isShot = item.type === "shot";
  const isCaseStudy = item.type === "case_study";

  const handleSetFeatured = () => {
    console.log('handleSetFeatured called for item:', item.id, 'type:', item.type);
    onSetFeatured(item.id);
  };

  const handleToggleStatus = () => {
    const newStatus = item.status === "draft" ? "published" : "draft";
    console.log('handleToggleStatus called for item:', item.id, 'newStatus:', newStatus);
    onUpdateStatus(item.id, newStatus);
  };


  const getTypeColorSquare = () => {
    if (isShot) return <div className="h-2 w-2 bg-amber-500" />;
    if (isCaseStudy) return <div className="h-2 w-2 bg-blue-500" />;
    return null;
  };

  const getTypeLabel = () => {
    if (isShot) return "Shot";
    if (isCaseStudy) return "Case Study";
    return "Item";
  };

  const getRelativeTime = () => {
    // When sorting by "Last Added", show "Added X ago" with created_at
    // When sorting by "Last Edited", show "Edited X ago" with updated_at
    // For other sort orders, default to "Edited X ago" with updated_at
    const isLastAdded = sortOrder === 'last_added';

    // Use created_at for "Last Added", updated_at for everything else
    const timestamp = isLastAdded ? item.created_at : (item.updated_at || item.created_at);
    const label = isLastAdded ? 'Added' : 'Edited';
    const timeAgo = formatDistanceToNow(new Date(timestamp), { addSuffix: true });

    return `${label} ${timeAgo}`;
  };

  return (
    <Card className="overflow-hidden transition-all duration-200 group relative h-full hover:shadow-lg" style={{
      contain: 'layout style',
      willChange: 'transform'
    }}>
      {item.is_featured && (
        <div className="absolute top-2 left-2 z-50 flex gap-1">
          <Badge className="text-[10.5px] py-1 px-2 rounded-md bg-green-500 text-green-900 hover:bg-green-500/80 transition-all duration-200 flex items-center">
            <Star className="h-3 w-3 fill-green-900 group-hover:mr-1 transition-all duration-200" />
            <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-200 whitespace-nowrap">Featured</span>
          </Badge>
        </div>
      )}

      {!item.is_featured && item.status === "draft" && (
        <div className="absolute top-2 left-2 z-50">
          <Badge className="rounded-md text-[10.5px] py-1 px-2 bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200 flex items-center">
            <File className="h-2.5 w-2.5 fill-gray-700 group-hover:mr-1 transition-all duration-200" />
            <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-200 whitespace-nowrap">Draft</span>
          </Badge>
        </div>
      )}

      {isDraggable && (
        <div className="absolute top-2 right-2 z-50 cursor-grab active:cursor-grabbing bg-white/90 rounded p-1 hover:bg-white transition-all opacity-0 group-hover:opacity-100">
          <img src="/drag.png" alt="" className="h-4 w-4" />
        </div>
      )}
      
      <div
        className="relative aspect-video w-full overflow-hidden bg-muted cursor-pointer"
        onClick={() => onEdit(item.id)}
      >
        {item.cover_image ? (
          <img
            src={item.cover_image}
            alt={item.title}
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.src = '/placeholder.svg';
            }}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-muted-foreground">
            {isShot ? "1600 × 1600" : "1600 × 900"}
          </div>
        )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
      </div>

      <CardContent className="p-0 py-4 px-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold line-clamp-1 text-ellipsis">{item.title}</h3>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                {getTypeColorSquare()}
                <span className="text-xs text-muted-foreground">{getTypeLabel()}</span>
              </div>

              <span className="flex items-center w-1 h-1 bg-slate-200 rounded-full"></span>

              <span className="text-xs text-muted-foreground">
                {getRelativeTime()}
              </span>
            </div>
           
          </div>
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" disabled={isProcessing}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => {
                  console.log('Edit clicked for item:', item.id, 'type:', item.type);
                  onEdit(item.id);
                }}>
                  <FileEdit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    console.log('Duplicate clicked for item:', item.id, 'type:', item.type);
                    onDuplicate(item.id);
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate
                </DropdownMenuItem>
                {item.status === "published" && (
                  <DropdownMenuItem onClick={handleToggleStatus}>
                    <Undo className="mr-2 h-4 w-4" />
                    Revert to Draft
                  </DropdownMenuItem>
                )}
                {item.status === "draft" && (
                  <DropdownMenuItem onClick={handleToggleStatus}>
                    <svg className="h-4 w-4 mr-2 flex-shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7.62027 0C11.8288 0 15.2405 3.41154 15.2405 7.6199C15.2405 7.82065 15.2327 8.01965 15.2174 8.21652C14.8838 7.89996 14.5061 7.62942 14.0944 7.41503C14.0803 6.96172 14.0197 6.52021 13.9171 6.09501L11.3586 6.09565C11.3832 6.35062 11.4017 6.61004 11.4138 6.87311C11.0208 6.9042 10.6408 6.98114 10.2789 7.09862C10.2668 6.75563 10.2429 6.42071 10.2084 6.09569H5.03209C4.98027 6.58353 4.95238 7.09375 4.95238 7.6199C4.95238 8.4271 5.01802 9.19681 5.13503 9.90558H7.2365C7.08594 10.2667 6.97684 10.6495 6.91535 11.0481H5.38107C5.83688 12.7356 6.60137 13.8931 7.4032 14.0725C7.61928 14.4924 7.89364 14.8774 8.21554 15.2169C8.01912 15.232 7.82057 15.2397 7.62027 15.2397C3.4117 15.2397 0 11.8282 0 7.6199C0 3.41154 3.4117 0 7.62027 0ZM4.19799 11.0485L2.12372 11.0484C2.85388 12.2164 3.94603 13.1349 5.24308 13.6469C4.84516 13.0222 4.51678 12.2404 4.27525 11.3495L4.19799 11.0485ZM3.88188 6.09564H1.32371L1.32008 6.10873C1.20421 6.59358 1.14286 7.09954 1.14286 7.6199C1.14286 8.42451 1.28959 9.19491 1.55776 9.90573L3.97541 9.90543C3.86699 9.18234 3.80952 8.41422 3.80952 7.6199C3.80952 7.09878 3.83426 6.58893 3.88188 6.09564ZM5.24389 1.59285L5.22643 1.59948C3.66658 2.22019 2.40485 3.42951 1.71558 4.95315L4.03796 4.95342C4.2767 3.61803 4.69335 2.45547 5.24389 1.59285ZM7.62027 1.1428L7.53211 1.14684C6.568 1.23504 5.63656 2.76027 5.20401 4.9531H10.0364C9.6051 2.76642 8.67771 1.24359 7.71642 1.14762L7.62027 1.1428ZM9.99741 1.59289L10.0788 1.72553C10.5886 2.57308 10.9758 3.68532 11.2025 4.95342L13.5249 4.95315C12.8661 3.4969 11.6844 2.32779 10.2193 1.68529L9.99741 1.59289ZM16 11.8097C16 14.124 14.1239 16 11.8095 16C9.49516 16 7.61905 14.124 7.61905 11.8097C7.61905 9.49547 9.49516 7.61944 11.8095 7.61944C14.1239 7.61944 16 9.49547 16 11.8097ZM11.4286 14.0953C11.4286 14.3057 11.5992 14.4763 11.8095 14.4763C12.0199 14.4763 12.1905 14.3057 12.1905 14.0953V10.4438L13.4449 11.6982C13.5937 11.8469 13.8349 11.8469 13.9837 11.6982C14.1324 11.5494 14.1324 11.3082 13.9837 11.1594L12.0789 9.25471C11.9301 9.106 11.6889 9.106 11.5401 9.25471L9.63535 11.1594C9.48663 11.3082 9.48663 11.5494 9.63535 11.6982C9.78415 11.8469 10.0254 11.8469 10.1742 11.6982L11.4286 10.4438V14.0953Z" fill="currentColor"/>
                    </svg>
                    Publish
                  </DropdownMenuItem>
                )}
                {item.is_featured ? (
                  <DropdownMenuItem disabled>
                    <Star className="mr-2 h-4 w-4 fill-current" />
                    Featured
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={handleSetFeatured}>
                    <Star className="mr-2 h-4 w-4" />
                    Set as Featured
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    console.log('Delete clicked for item:', item.id, 'type:', item.type);
                    onRemove(item.id);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UnifiedPortfolioCard;
